/**
 * R6.3-B Phase 2.1 — the single sector display-name authority.
 *
 * Three distinct concepts that must never be conflated:
 *
 *   1. TECHNICAL IDENTITY   — `siteName` + `sectorCode` (and the backend
 *      sector_id behind them). Immutable. The only thing joins may use.
 *   2. CANONICAL BUSINESS NAME — the sector's real parcel name(s), from
 *      `cgm_pilot_topology.json`. Read-only truth.
 *   3. CUSTOM DISPLAY NAME  — an optional operator override, stored in a
 *      presentation overlay.
 *
 * Resolution, in one place so Dashboard / Sectors / Sector Detail can never
 * disagree about what a sector is called:
 *
 *     effective = custom ?? canonical ?? technical fallback
 *
 * The technical fallback is a defensive last resort, not a label we expect to
 * show: every CGM sector has at least one canonical parcel name.
 *
 * Pure functions only — no React, no network, no side effects.
 */

/** Separator between the parcel names of a multi-parcel sector. */
export const PARCEL_NAME_SEPARATOR = ' · ';

/** Maximum length of a custom name, measured after trimming. Mirrors the API. */
export const MAX_CUSTOM_NAME_LENGTH = 80;

/**
 * Canonical business name for a sector: every parcel it irrigates, joined.
 *
 * A sector may irrigate several parcels, and dropping any of them would be a
 * silent lie about what the operator is turning on. Returns null when the
 * topology declares no parcel, so the caller falls back rather than showing "".
 */
export function canonicalDisplayName(parcelNames: readonly string[]): string | null {
  const named = parcelNames.filter((name) => typeof name === 'string' && name.trim() !== '');
  if (named.length === 0) return null;
  return named.join(PARCEL_NAME_SEPARATOR);
}

/**
 * Defensive technical label, used only when no canonical name exists.
 * Kept identical to the pre-naming-layer title so a fallback is recognisable.
 */
export function technicalFallbackName(siteName: string, sectorCode: string): string {
  return `${siteName} — ${sectorCode}`;
}

/** True when a custom name is usable. Empty/blank is a mistake, not a reset. */
export function isValidCustomDisplayName(raw: string | null | undefined): boolean {
  if (typeof raw !== 'string') return false;
  const trimmed = raw.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_CUSTOM_NAME_LENGTH;
}

/**
 * Trim a candidate custom name, or null if it is not usable.
 *
 * Only leading/trailing whitespace is removed. Accents and interior punctuation
 * are preserved byte-for-byte — `Marselan Barthès` must never become
 * `Marselan Barthes`.
 */
export function normalizeCustomDisplayName(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0 || trimmed.length > MAX_CUSTOM_NAME_LENGTH) return null;
  return trimmed;
}

export interface SectorDisplayNameInput {
  /** Operator override, when one is stored and valid. */
  customDisplayName?: string | null;
  /** Canonical parcel-derived name, when the topology provides one. */
  canonicalDisplayName?: string | null;
  siteName: string;
  technicalSectorCode: string;
}

/**
 * The effective, farmer-facing sector name.
 *
 * An invalid stored custom name (blank, over-long, wrong type) is ignored rather
 * than rendered: bad metadata degrades to the canonical name, it never blanks a
 * sector title.
 */
export function resolveSectorDisplayName(input: SectorDisplayNameInput): string {
  const custom = normalizeCustomDisplayName(input.customDisplayName);
  if (custom !== null) return custom;

  const canonical = input.canonicalDisplayName;
  if (typeof canonical === 'string' && canonical.trim() !== '') return canonical;

  return technicalFallbackName(input.siteName, input.technicalSectorCode);
}

/** True when the effective name comes from an operator override. */
export function hasCustomDisplayName(customDisplayName?: string | null): boolean {
  return normalizeCustomDisplayName(customDisplayName) !== null;
}

/**
 * Roles permitted to rename, mirroring the API's `BUSINESS_METADATA_ROLES`
 * (including the backend's own role aliases: `coop_admin`/`org_admin` → admin,
 * `farmer` → farm_manager).
 *
 * This is a COURTESY gate that hides a control the caller cannot use. It is not
 * the security boundary — `PUT /iot/sector-display-names` enforces the same set
 * server-side and would reject the request regardless of what the UI shows.
 */
const RENAME_ROLES = new Set([
  'owner',
  'admin',
  'coop_admin',
  'org_admin',
  'farm_manager',
  'farmer',
]);

export function canRenameSector(role: string | null | undefined): boolean {
  return typeof role === 'string' && RENAME_ROLES.has(role.trim().toLowerCase());
}

/** Map key for per-sector metadata. Identical to `SoverdiSectorView.id`. */
export function sectorNameKey(siteName: string, sectorCode: string): string {
  return `${siteName}:${sectorCode}`;
}

/** One stored override, as returned by `GET /iot/sector-display-names`. */
export interface SectorDisplayNameRow {
  site_name: string;
  sector_code: string;
  custom_display_name: string | null;
}

/**
 * Index API rows by sector key, keeping only usable names.
 *
 * A row whose name is blank or over-long is dropped here rather than at render
 * time, so every consumer sees the same already-validated view of the overlay.
 */
export function indexCustomNames(
  rows: readonly SectorDisplayNameRow[],
): Map<string, string> {
  const index = new Map<string, string>();
  for (const row of rows) {
    // Both coordinates must be present: a row missing either cannot identify a
    // sector, and indexing it would create an orphan key that silently matches
    // nothing.
    if (
      !row
      || typeof row.site_name !== 'string'
      || typeof row.sector_code !== 'string'
      || row.site_name.trim() === ''
      || row.sector_code.trim() === ''
    ) {
      continue;
    }
    const normalized = normalizeCustomDisplayName(row.custom_display_name);
    if (normalized === null) continue;
    index.set(sectorNameKey(row.site_name, row.sector_code), normalized);
  }
  return index;
}
